import { useEffect, useRef, useState } from 'react';
import type { ClockConfig, HandicapConfig, Ruleset } from '@xiangqi/protocol';
import { MODE_INFO } from '@xiangqi/protocol';
import { api, ensureSession } from '../api.ts';
import { generateSimulatedPlayer } from '../game/fake-player.ts';

interface MatchmakingModalProps {
  ruleset: Ruleset;
  clock?: ClockConfig | undefined;
  handicap?: HandicapConfig | undefined;
  onClose(): void;
  navigate(path: string): void;
}

export function MatchmakingModal({
  ruleset,
  clock = { initial_ms: 600_000, increment_ms: 5_000 },
  handicap,
  onClose,
  navigate,
}: MatchmakingModalProps) {
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [matchedPlayerName, setMatchedPlayerName] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  const modeName = MODE_INFO[ruleset]?.label ?? 'Cờ Tướng';

  useEffect(() => {
    cancelledRef.current = false;
    const startTime = Date.now();

    // Đồng hồ đếm thời gian tìm kiếm
    const timer = setInterval(() => {
      setSecondsElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    let pollInterval: ReturnType<typeof setInterval> | null = null;

    const startMatchmaking = async () => {
      try {
        await ensureSession();
      } catch {
        // Tiếp tục ngay cả khi offline
      }

      const poll = async () => {
        if (cancelledRef.current) return;
        try {
          const res = await api.findMatch(ruleset, clock, handicap);
          if (res.matched && res.room_code) {
            if (pollInterval) clearInterval(pollInterval);
            clearInterval(timer);
            setMatchedPlayerName('Đối thủ online');
            setTimeout(() => {
              if (!cancelledRef.current) {
                navigate(`/r/${res.room_code}`);
              }
            }, 600);
            return;
          }
        } catch {
          // Bỏ qua lỗi mạng khi polling
        }

        // Sau 6 giây tìm kiếm không có người -> tự động ghép với Bot Server (Gen 32 NNUE)
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        if (elapsed >= 6) {
          if (pollInterval) clearInterval(pollInterval);
          clearInterval(timer);

          // Lọc bỏ handicap không tương thích với ruleset (ví dụ Cờ Úp thường không có asym_royal)
          const isRoyalAsym =
            handicap &&
            handicap.type === 'asym_royal' &&
            (ruleset === 'blind_si_chua' || ruleset === 'blind_tuong_vuong');

          const effectiveHandicap =
            handicap &&
            (isRoyalAsym ||
              ((handicap.type === 'piece' || handicap.type === 'moves') && ruleset === 'standard') ||
              (handicap.type === 'none' && handicap.time))
              ? handicap
              : undefined;

          // Xác định bên đi trước:
          let assignedSide: 'red' | 'black' = Math.random() < 0.5 ? 'red' : 'black';
          if (isRoyalAsym && effectiveHandicap) {
            assignedSide = effectiveHandicap.giver_side === 'red' ? 'red' : 'black';
          }

          try {
            localStorage.setItem(
              'xq_saved_config',
              JSON.stringify({
                ruleset,
                playerSide: assignedSide,
                side: assignedSide,
                handicap: effectiveHandicap ?? {
                  type: 'none',
                  giver_side: 'red',
                  pieces: [],
                  moves_count: 2,
                },
                clock,
              }),
            );
          } catch {
            // ignore
          }

          setMatchedPlayerName('Bot Server (Gen 32)');
          void api.cancelMatchmaking().catch(() => {});

          try {
            // Tạo phòng online thật để BotMaster vào chơi với Native C++ Belief-NNUE
            const res = await api.createRoom(ruleset, clock, {
              vs_bot: { profile: 'master', human_side: assignedSide },
              ...(effectiveHandicap ? { handicap: effectiveHandicap } : {}),
            });
            if (!cancelledRef.current) {
              setTimeout(() => {
                if (!cancelledRef.current) {
                  navigate(`/r/${res.room.room_code}`);
                }
              }, 400);
              return;
            }
          } catch (e) {
            console.error('Không tạo được phòng bot online, fallback local:', e);
            if (!cancelledRef.current) {
              const sim = generateSimulatedPlayer();
              setMatchedPlayerName(sim.displayName);
              setTimeout(() => {
                if (!cancelledRef.current) {
                  navigate(`/m/local-quickplay-${Date.now()}`);
                }
              }, 600);
            }
          }
        }
      };

      // Poll ngay lần đầu
      void poll();
      pollInterval = setInterval(() => {
        void poll();
      }, 1500);
    };

    void startMatchmaking();

    return () => {
      cancelledRef.current = true;
      clearInterval(timer);
      if (pollInterval) clearInterval(pollInterval);
      void api.cancelMatchmaking().catch(() => {});
    };
  }, [ruleset, clock, handicap, navigate]);

  const handleCancel = () => {
    cancelledRef.current = true;
    void api.cancelMatchmaking().catch(() => {});
    onClose();
  };

  const formatTimer = (s: number) => {
    const mins = Math.floor(s / 60)
      .toString()
      .padStart(2, '0');
    const secs = (s % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) handleCancel(); }}>
      <div className="modal matchmaking-modal" role="dialog" aria-modal="true">
        <div className="matchmaking-content">
          <div className="matchmaking-radar">
            <div className="radar-circle circle-1" />
            <div className="radar-circle circle-2" />
            <div className="radar-circle circle-3" />
            <div className="radar-piece-center">
              <span>{ruleset === 'standard' ? '帥' : ruleset === 'blind_si_chua' ? '仕' : ruleset === 'blind_tuong_vuong' ? '相' : '包'}</span>
            </div>
          </div>

          <div className="matchmaking-info">
            <span className="matchmaking-mode-pill">{modeName}</span>
            {matchedPlayerName ? (
              <h2 className="matchmaking-status-title matched">
                ✓ Đã ghép trận với {matchedPlayerName}!
              </h2>
            ) : (
              <h2 className="matchmaking-status-title">Đang tìm đối thủ...</h2>
            )}
            <p className="matchmaking-timer">{formatTimer(secondsElapsed)}</p>
            <p className="matchmaking-hint">
              {matchedPlayerName ? 'Đang vào bàn cờ…' : 'Hệ thống đang tìm kiếm kỳ thủ có cùng trình độ.'}
            </p>
          </div>

          <button
            type="button"
            className="matchmaking-cancel-btn"
            onClick={handleCancel}
            disabled={!!matchedPlayerName}
          >
            Hủy tìm trận
          </button>
        </div>
      </div>
    </div>
  );
}
